import { normalRingProVariantGid } from 'content/products/ring-pro/bestBuyPickupVariants';
import {
  RING_COLOR_TO_CHARGER_MAP,
  type RingProChargerCaseColor,
  type RingSizes,
} from 'content/products/ring-pro/constants';
import { ringProFromOpsSkuCode } from 'content/products/ring-pro/shipByConfig';
import { generateBundleId } from 'components/shared/Cart/bundle';
import { ProductType } from 'components/shared/Cart/types';
import { partitionPickupLines } from 'lib/commerce/cart/bestBuyPickup';
import {
  BB_LINE_PROPS,
  BB_ORDER_REFERENCE_KEY,
} from './config';

type Attr = { key: string; value: string };
type CartRecord = Record<string, any>;

const attrValue = (attrs: Attr[] | undefined, key: string): string | undefined =>
  attrs?.find((a) => a.key === key)?.value;

export const PICKUP_ATTRIBUTE_KEYS: string[] = [
  ...Object.values(BB_LINE_PROPS),
  BB_ORDER_REFERENCE_KEY,
];

/** Everything the pickup order put on the ring line: the pickup properties
 * and the one-ring cap (`_max_quantity`, set in pickupLineItems.ts). A ring
 * switched to shipping sheds all of it: shipping orders have no such cap,
 * so the same ring can be added again (the normal flow). */
const PICKUP_LINE_KEYS: string[] = [...PICKUP_ATTRIBUTE_KEYS, '_max_quantity'];

export type PickupLine = {
  lineId: string;
  locationId: string;
  storeName: string;
  zip: string;
  sku: string;
  quantity: number;
  attributes: Attr[];
};

const cartNodes = (cart: CartRecord | null | undefined): any[] =>
  (cart?.lines?.edges ?? []).map((edge: any) => edge.node).filter(Boolean);

export const getPickupLines = (cart: CartRecord | null | undefined): PickupLine[] =>
  cartNodes(cart).flatMap((node) => {
    const attrs: Attr[] = node.attributes ?? [];
    const locationId = attrValue(attrs, BB_LINE_PROPS.LOCATION_ID);
    if (!locationId) return [];
    return [
      {
        lineId: String(node.id),
        locationId,
        storeName: attrValue(attrs, BB_LINE_PROPS.STORE) ?? '',
        zip: attrValue(attrs, BB_LINE_PROPS.ZIP) ?? '',
        sku: attrValue(attrs, BB_LINE_PROPS.SKU) ?? '',
        quantity: Number(node.quantity ?? 1),
        attributes: attrs,
      },
    ];
  });

/** The cart's lines (id and quantity), to tell when the cart changed. */
export const cartLinesSignature = (cart: CartRecord | null | undefined): string =>
  cartNodes(cart)
    .map((node) => `${node.id}:${node.quantity ?? 1}`)
    .join('|');

export const pickupKey = (cart: CartRecord | null | undefined): string | null => {
  const [line] = getPickupLines(cart);
  return line ? `${line.locationId}|${line.sku}` : null;
};

export type PickupCartVerdict =
  | { kind: 'none' }
  /** `companionLineIds`: legacy charger lines (carts built before the
   * pickup order dropped the charger) that checkout removes while stamping
   * the hold. `allowedLineIds`: the ring's funnel add-ons and MAX, which
   * stay on the order. Both empty for a ring alone. */
  | {
      kind: 'ok';
      line: PickupLine;
      companionLineIds: string[];
      allowedLineIds: string[];
    }
  | { kind: 'multi' };

/** One-ring rule: a pickup order is one ring, quantity 1, plus its own
 * funnel add-ons (MAX, Clue, Luminary CNO Pro: same `_bundle_id`) or a MAX
 * added on its own (lib/commerce/cart/bestBuyPickup.ts decides). A legacy
 * charger line (`_mirror_parent_qty`) is tolerated and reported in
 * `companionLineIds` so checkout can drop it. Anything else is `multi`: the
 * cart is not a pickup order. */
export const analyzePickupCart = (
  cart: CartRecord | null | undefined,
): PickupCartVerdict => {
  const pickupLines = getPickupLines(cart);
  if (pickupLines.length === 0) return { kind: 'none' };
  const [line] = pickupLines;
  if (pickupLines.length > 1 || line.quantity !== 1) return { kind: 'multi' };
  const partition = partitionPickupLines(cartNodes(cart));
  if (partition.foreign.length > 0 || !line.sku || !line.zip) {
    return { kind: 'multi' };
  }
  return {
    kind: 'ok',
    line,
    companionLineIds: partition.chargers.map((node) => String(node.id)),
    allowedLineIds: partition.allowed.map((node) => String(node.id)),
  };
};

export type PrunePlan = {
  mutation: Mutation;
  /** Lines removed (foreign to the pickup order). */
  removedLineIds: string[];
};

/** Request that turns a `multi` cart with exactly one pickup ring back into
 * a pickup order: foreign lines leave, and a quantity above 1 on the ring
 * comes back to 1 (one ring per pickup order). Null when the cart has no
 * single pickup ring to keep, or nothing to change: the `multi` notice then
 * stays. The last line of defence before the Best Buy hold, for carts the
 * add policy never saw (built before the rule, or in another tab). */
export const buildPruneMutation = (cart: CartRecord): PrunePlan | null => {
  const pickupLines = getPickupLines(cart);
  if (pickupLines.length !== 1) return null;
  const [line] = pickupLines;
  const partition = partitionPickupLines(cartNodes(cart));
  const removedLineIds = partition.foreign.map((node) => String(node.id));
  const resetQuantity = line.quantity !== 1;
  if (removedLineIds.length === 0 && !resetQuantity) return null;
  return {
    removedLineIds,
    mutation: {
      ...(removedLineIds.length ? { lineItemsToRemove: removedLineIds } : {}),
      ...(resetQuantity
        ? {
            lineItemsToModify: [
              { id: line.lineId, quantity: 1, attributes: line.attributes },
            ],
          }
        : {}),
    },
  };
};

/** True when a hold reference sits on the cart or on any line (stale after a
 * switch to shipping, an expired hold, or a removed ring). */
export const hasOrderReference = (cart: CartRecord | null | undefined): boolean =>
  attrValue(cart?.attributes, BB_ORDER_REFERENCE_KEY) !== undefined ||
  cartNodes(cart).some(
    (node) => attrValue(node.attributes, BB_ORDER_REFERENCE_KEY) !== undefined,
  );

const without = (attrs: Attr[] | undefined, keys: string[]): Attr[] =>
  (attrs ?? []).filter((a) => !keys.includes(a.key));

const withValue = (attrs: Attr[] | undefined, key: string, value: string): Attr[] => [
  ...without(attrs, [key]),
  { key, value },
];

export type Mutation = {
  lineItemsToModify?: {
    id: string;
    attributes: Attr[];
    merchandiseId?: string;
    quantity?: number;
  }[];
  lineItemsToAdd?: {
    merchandiseId: string;
    quantity: number;
    attributes: Attr[];
  }[];
  lineItemsToRemove?: string[];
  cartAttributes?: Attr[];
};

/** Request that writes the hold reference on the pickup line and on the cart.
 * `lineItemsToRemove` (legacy charger lines) go in the same request, so the
 * cart never sits between "held" and "one ring". */
export const buildStampMutation = (
  cart: CartRecord,
  line: PickupLine,
  orderReference: string,
  lineItemsToRemove: string[] = [],
): Mutation => ({
  lineItemsToModify: [
    {
      id: line.lineId,
      attributes: withValue(line.attributes, BB_ORDER_REFERENCE_KEY, orderReference),
    },
  ],
  ...(lineItemsToRemove.length ? { lineItemsToRemove } : {}),
  cartAttributes: withValue(cart.attributes, BB_ORDER_REFERENCE_KEY, orderReference),
});

/** Request that removes the hold reference from the cart and every line.
 * Null when there is nothing to do. */
export const buildClearMutation = (cart: CartRecord): Mutation | null => {
  const keys = [BB_ORDER_REFERENCE_KEY];
  const lines = cartNodes(cart).flatMap((node) => {
    const attrs: Attr[] = node.attributes ?? [];
    if (!attrs.some((a) => keys.includes(a.key))) return [];
    return [{ id: String(node.id), attributes: without(attrs, keys) }];
  });
  const cartHasRef = attrValue(cart.attributes, BB_ORDER_REFERENCE_KEY) !== undefined;
  if (lines.length === 0 && !cartHasRef) return null;
  return {
    ...(lines.length ? { lineItemsToModify: lines } : {}),
    ...(cartHasRef
      ? { cartAttributes: without(cart.attributes, [BB_ORDER_REFERENCE_KEY]) }
      : {}),
  };
};

/** The US charger variant gid for a case colour and ring size, or undefined.
 * Injected (the real table is content/products/ring-pro/ringProChargerCaseVariants,
 * which does not load in the plain-node test harness). */
export type ChargerVariantLookup = (
  caseColor: RingProChargerCaseColor,
  size: RingSizes,
) => string | undefined;

export type SwitchToShippingPlan =
  /** `mutation` null: nothing to change. `chargerCase`: the case colour of
   * the charger being added (null when none is added), for the confirmation. */
  | {
      ok: true;
      mutation: Mutation | null;
      chargerCase: RingProChargerCaseColor | null;
    }
  /** A charger variant could not be resolved: mutate nothing. */
  | { ok: false };

/** Request for the switch to shipping. Every pickup ring goes back to its
 * normal variant without the pickup properties (and the hold reference leaves
 * the cart). The pickup order carried no charger, so each ring gets its free
 * one back: the case its colour pairs with, tagged into the ring's bundle
 * (quantity mirrors the ring's). A charger already in that bundle is not
 * doubled. Fails closed when a variant cannot be resolved. */
export const buildSwitchToShippingMutation = (
  cart: CartRecord,
  chargerVariantFor: ChargerVariantLookup,
): SwitchToShippingPlan => {
  const keys = PICKUP_LINE_KEYS;
  const nodes = cartNodes(cart);
  const modify: NonNullable<Mutation['lineItemsToModify']> = [];
  const add: NonNullable<Mutation['lineItemsToAdd']> = [];
  let chargerCase: RingProChargerCaseColor | null = null;

  for (const node of nodes) {
    const attrs: Attr[] = node.attributes ?? [];
    if (!attrs.some((a) => PICKUP_ATTRIBUTE_KEYS.includes(a.key))) continue;
    const sku = attrValue(attrs, BB_LINE_PROPS.SKU) ?? '';
    const normal = normalRingProVariantGid(sku);
    const swap =
      normal && normal !== node.merchandise?.id ? { merchandiseId: normal } : {};
    let ringAttrs = without(attrs, keys);

    const bundleId = attrValue(attrs, '_bundle_id');
    const hasCharger =
      bundleId !== undefined &&
      nodes.some(
        (other) =>
          String(other.id) !== String(node.id) &&
          attrValue(other.attributes, '_bundle_id') === bundleId &&
          attrValue(other.attributes, '_mirror_parent_qty') === '1',
      );
    if (!hasCharger) {
      const parsed = ringProFromOpsSkuCode(sku);
      const caseColor = parsed ? RING_COLOR_TO_CHARGER_MAP[parsed.color] : undefined;
      const variantId =
        parsed && caseColor ? chargerVariantFor(caseColor, parsed.size) : undefined;
      if (!caseColor || !variantId) return { ok: false };
      const bundle = bundleId ?? generateBundleId();
      ringAttrs = withValue(withValue(ringAttrs, '_bundle_id', bundle), '_is_bundle_parent', '1');
      add.push({
        merchandiseId: variantId,
        quantity: Math.max(1, Number(node.quantity ?? 1)),
        attributes: [
          { key: '_product_type', value: ProductType.RING },
          { key: '_bundle_id', value: bundle },
          { key: '_mirror_parent_qty', value: '1' },
        ],
      });
      chargerCase ??= caseColor;
    }
    modify.push({ id: String(node.id), attributes: ringAttrs, ...swap });
  }

  const cartHasRef = attrValue(cart.attributes, BB_ORDER_REFERENCE_KEY) !== undefined;
  if (modify.length === 0 && !cartHasRef) {
    return { ok: true, mutation: null, chargerCase: null };
  }
  return {
    ok: true,
    chargerCase,
    mutation: {
      ...(modify.length ? { lineItemsToModify: modify } : {}),
      ...(add.length ? { lineItemsToAdd: add } : {}),
      ...(cartHasRef
        ? { cartAttributes: without(cart.attributes, [BB_ORDER_REFERENCE_KEY]) }
        : {}),
    },
  };
};
